import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import type { MyAssignment, Unavailability } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { formatDayHeading, formatTime } from '../calendar/dates';
import { useMe } from '../session/SessionProvider';
import { ASSIGNMENT_STATUS, formatDay } from './labels';

const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Mis asignaciones: dónde me asignaron (acepto o declino) y cuándo no puedo servir. */
export function MyServicePage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;
  return (
    <div className="page">
      <header className="page__header">
        <h1>Mis asignaciones</h1>
        <p className="muted">Dónde te asignaron y cuándo no puedes servir.</p>
      </header>
      <MyAssignments churchId={churchId} />
    </div>
  );
}

/** Lo de la página, también como pestaña «Mis asignaciones» de Mi vocación. */
export function MyAssignments({ churchId }: { churchId: string }) {
  const assignments = useAsync(
    (signal) => (churchId ? api.get<{ items: MyAssignment[] }>(`/assignments/churches/${churchId}/me/assignments`, signal).then((r) => r.items) : Promise.resolve([])),
    [churchId],
  );
  const unavailability = useAsync(
    (signal) => (churchId ? api.get<{ items: Unavailability[] }>(`/assignments/churches/${churchId}/me/unavailability`, signal).then((r) => r.items) : Promise.resolve([])),
    [churchId],
  );

  return (
    <>
      <Card title="Cultos donde me toca servir">
        {assignments.status === 'loading' ? <Loading /> : null}
        {assignments.status === 'error' ? <ErrorState error={assignments.error} onRetry={assignments.reload} /> : null}
        {assignments.status === 'ready' && assignments.data.length === 0 ? <EmptyState title="No tienes asignaciones próximas." /> : null}
        {assignments.status === 'ready' && assignments.data.length > 0 ? (
          <ul className="list">
            {assignments.data.map((a) => (
              <MyAssignmentRow key={a.id} churchId={churchId} assignment={a} onChange={assignments.reload} />
            ))}
          </ul>
        ) : null}
      </Card>

      <Card title="No puedo servir">
        {unavailability.status === 'loading' ? <Loading /> : null}
        {unavailability.status === 'error' ? <ErrorState error={unavailability.error} onRetry={unavailability.reload} /> : null}
        {unavailability.status === 'ready' ? (
          <>
            {unavailability.data.length === 0 ? <p className="muted">No has avisado fechas.</p> : null}
            <ul className="list">
              {unavailability.data.map((u) => (
                <UnavailabilityRow key={u.id} churchId={churchId} item={u} onChange={unavailability.reload} />
              ))}
            </ul>
            <UnavailabilityForm churchId={churchId} onAdded={unavailability.reload} />
          </>
        ) : null}
      </Card>
    </>
  );
}

function MyAssignmentRow({ churchId, assignment: a, onChange }: { churchId: string; assignment: MyAssignment; onChange: () => void }) {
  const [mode, setMode] = useState<'idle' | 'decline'>('idle');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = a.occasion.status !== 'canceled' && new Date(a.occasion.endsAt) > new Date();

  async function run(action: 'accept' | 'decline', reason?: string) {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/assignments/churches/${churchId}/assignments/${a.id}/${action}`, action === 'decline' ? { reason: reason || null } : undefined);
      setMode('idle');
      onChange();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  function onDecline(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void run('decline', String(new FormData(event.currentTarget).get('reason') || ''));
  }

  return (
    <li className="list__item service">
      <div className="service__main">
        <span className="service__when">
          {formatDayHeading(new Date(a.occasion.startsAt))} · {formatTime(a.occasion.startsAt)}
        </span>
        <strong>{a.role}</strong>
        <span className="muted small">
          {a.occasion.title}
          {a.departmentName ? ` · ${a.departmentName}` : ''}
        </span>
        {a.occasion.status === 'canceled' ? <span className="muted small">El culto fue cancelado.</span> : null}
        <Link className="small" to={`/app/cultos/${a.occasion.id}`} aria-label={`Ver culto: ${a.occasion.title}`}>
          Ver culto
        </Link>
      </div>
      <span className={`badge ${ASSIGNMENT_STATUS[a.status].tone}`}>{ASSIGNMENT_STATUS[a.status].label}</span>
      {open && mode === 'idle' && a.status !== 'declined' ? (
        <div className="actions">
          {a.status === 'requested' ? (
            <Button busy={busy} onClick={() => run('accept')}>
              Aceptar
            </Button>
          ) : null}
          <Button variant="ghost" onClick={() => setMode('decline')}>
            No puedo
          </Button>
        </div>
      ) : null}
      {mode === 'decline' ? (
        <form className="form form--inline service__cancel" onSubmit={onDecline} noValidate>
          <Field label="Motivo (opcional)" name="reason" maxLength={500} />
          <Button type="submit" variant="danger" busy={busy}>
            Confirmar que no puedo
          </Button>
          <Button type="button" variant="ghost" onClick={() => setMode('idle')}>
            Volver
          </Button>
        </form>
      ) : null}
      {error ? <Alert>{error}</Alert> : null}
    </li>
  );
}

function UnavailabilityRow({ churchId, item: u, onChange }: { churchId: string; item: Unavailability; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  async function withdraw() {
    setBusy(true);
    try {
      await api.post(`/assignments/churches/${churchId}/me/unavailability/${u.id}/withdraw`);
      onChange();
    } finally {
      setBusy(false);
    }
  }
  return (
    <li className="list__item service">
      <div className="service__main">
        <strong>{u.startsOn === u.endsOn ? formatDay(u.startsOn) : `${formatDay(u.startsOn)} – ${formatDay(u.endsOn)}`}</strong>
        {u.note ? <span className="muted small">{u.note}</span> : null}
      </div>
      <Button variant="ghost" busy={busy} onClick={withdraw}>
        Quitar
      </Button>
    </li>
  );
}

function UnavailabilityForm({ churchId, onAdded }: { churchId: string; onAdded: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const today = isoDay(new Date());

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const startsOn = String(form.get('startsOn'));
    setBusy(true);
    setError(null);
    try {
      await api.post(`/assignments/churches/${churchId}/me/unavailability`, {
        startsOn,
        endsOn: String(form.get('endsOn') || '') || startsOn,
        note: String(form.get('note') || '') || null,
      });
      formEl.reset();
      onAdded();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form" onSubmit={onSubmit} noValidate>
      <h3>Avisar fechas</h3>
      {error ? <Alert>{error}</Alert> : null}
      <div className="form__row">
        <Field label="Desde" name="startsOn" type="date" required min={today} defaultValue={today} />
        <Field label="Hasta" name="endsOn" type="date" min={today} hint="Déjalo vacío si es un solo día." />
      </div>
      <Field label="Nota (opcional)" name="note" maxLength={300} placeholder="Viaje, trabajo…" />
      <Button type="submit" variant="secondary" busy={busy}>
        Avisar
      </Button>
    </form>
  );
}
